/**
 * ControlBoothPage — the venue's live Home (`/`).
 *
 * ITEM 3 of the Master Control Center rollout (Feb 2026): the layout was
 * redesigned in /booth-preview and approved by the user. This file wires
 * that visual shell to LIVE data (MusicContext + backend). Nothing about
 * the data flow changed from the previous version — only presentation:
 *   1. Master Player   — currentTrack + transport (from MusicContext)
 *   2. Song List       — tracks in the currently selected day-playlist
 *   3. Request Inbox   — reuses <RequestsInbox />
 *   4. Vibe Picker     — GET /api/playlists/my-schedule filtered to today
 *   5. Search Bar      — GET /api/music/search + injectNext
 *   6. Celebrate hub   — GET /api/venue/celebrations → injectNext
 *
 * Design tokens: dark #09090B, zinc-900 cards, fuchsia-500 accent for the
 * Master Player / active states, pink-500 for the Celebrate hub, cyan-400
 * for the live-requests dot. Font stack: Outfit for titles, JetBrains
 * mono via `font-mono` for tracking-widest labels.
 *
 * IMPORTANT: All data-testid values from the previous version are
 * preserved so existing regression tests continue to pass.
 */
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import axios from 'axios';
import { AnimatePresence, motion } from 'framer-motion';
import { toast } from 'sonner';
import {
  Search, Play, Pause, SkipBack, SkipForward, Sparkles, Music2,
  Loader2, Cake, PartyPopper, X, ListMusic, AudioLines, ChevronDown,
  Video as VideoIcon,
} from 'lucide-react';

import Layout from '../components/Layout';
import RequestsInbox from '../components/RequestsInbox';
import ShoutoutsInbox from '../components/ShoutoutsInbox';
import DeviceBadge from '../components/DeviceBadge';
import { useMusic } from '../contexts/MusicContext';
import BoothAlbumArt from '../components/BoothAlbumArt';
import AlwaysOnMode from '../components/AlwaysOnMode';
import { useAuth } from '../contexts/AuthContext';
import { Navigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Weekday key that matches backend schedule shape (VALID_DAYS in server.py).
const _DAY_KEYS = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
const todayKey = () => _DAY_KEYS[new Date().getDay()];

const fmtDuration = (ms) => {
  if (!ms || ms < 0) return '';
  const s = Math.floor(ms / 1000);
  const m = Math.floor(s / 60);
  return `${m}:${String(s % 60).padStart(2, '0')}`;
};

// ─────────────────────────────────────────────────────────────────────────
// Master Player — desktop vertical / mobile horizontal card
// ─────────────────────────────────────────────────────────────────────────
const MasterPlayer = ({ fill = true }) => {
  const { currentTrack, isPlaying, progressMs, durationMs, play, pause, nextTrack, previousTrack } = useMusic();
  const art = currentTrack?.album_image || currentTrack?.artwork_url || '';
  const name = currentTrack?.name || 'Nothing playing';
  const artist = Array.isArray(currentTrack?.artists) ? currentTrack.artists.join(', ') : (currentTrack?.artist_name || '');
  const album = currentTrack?.album_name || '';
  const pct = durationMs > 0 ? Math.min(100, (progressMs / durationMs) * 100) : 0;

  return (
    <section
      className={`bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col ${fill ? 'h-full min-h-0' : ''}`}
      data-testid="control-booth-master-player"
    >
      <div className="px-4 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Master Player</span>
        <div className="flex items-center gap-2">
          <DeviceBadge compact />
          <span className="flex items-center gap-1 text-[10px] text-fuchsia-400 font-mono">
            <span className={`w-1.5 h-1.5 bg-fuchsia-500 rounded-full ${isPlaying ? 'animate-pulse' : ''}`} />
            {isPlaying ? 'LIVE' : 'IDLE'}
          </span>
        </div>
      </div>

      <div className={`p-4 flex flex-col ${fill ? 'flex-1 min-h-0' : ''}`}>
        {/* Now-playing tile — the current track's album art. (The on-screen
            video was removed: it was a separate, independently-looping YouTube
            player that never matched the audio's play/pause. Audio comes from
            the hidden dual-driver in utils/youtubePlayer.js.) */}
        <div className={`relative w-full aspect-square rounded-xl overflow-hidden border border-zinc-800 shadow-2xl flex-shrink-0 ${fill ? '' : 'max-w-[min(72vw,360px)] mx-auto'}`}>
          <BoothAlbumArt videoId={currentTrack?.id} albumImage={art} alt={name} />
          <div className="absolute inset-0 bg-gradient-to-t from-black/60 via-transparent to-transparent pointer-events-none" />
        </div>

        {/* Meta */}
        <div className="mt-4 flex-shrink-0">
          <h2 className="text-xl font-black tracking-tight truncate" style={{ fontFamily: 'Outfit' }} data-testid="master-track-name">{name}</h2>
          <p className="text-zinc-400 text-sm truncate" data-testid="master-track-artist">
            {artist || '—'}{album ? ` · ${album}` : ''}
          </p>
        </div>

        {/* Progress */}
        <div className="mt-3 flex-shrink-0">
          <div className="h-1 bg-zinc-800 rounded-full overflow-hidden">
            <div className="h-full bg-fuchsia-500" style={{ width: `${pct}%` }} />
          </div>
          <div className="flex justify-between text-[10px] font-mono text-zinc-500 mt-1">
            <span>{fmtDuration(progressMs)}</span>
            <span>{fmtDuration(durationMs)}</span>
          </div>
        </div>

        {/* Transport */}
        <div className="mt-4 flex items-center justify-center gap-3 flex-shrink-0">
          <button
            onClick={() => previousTrack?.()}
            className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            data-testid="master-prev"
            aria-label="Previous"
          >
            <SkipBack className="w-5 h-5 text-white" />
          </button>
          <button
            onClick={() => (isPlaying ? pause?.() : play?.())}
            className="w-16 h-16 rounded-full bg-fuchsia-500 hover:bg-fuchsia-400 flex items-center justify-center shadow-[0_0_30px_rgba(217,70,239,0.5)] hover:scale-105 active:scale-95 transition-[transform,background-color]"
            data-testid="master-playpause"
            aria-label={isPlaying ? 'Pause' : 'Play'}
          >
            {isPlaying
              ? <Pause className="w-6 h-6 fill-current text-black" />
              : <Play className="w-7 h-7 fill-current text-black ml-0.5" />}
          </button>
          <button
            onClick={() => nextTrack?.()}
            className="w-11 h-11 rounded-full bg-zinc-800 hover:bg-zinc-700 flex items-center justify-center transition-colors"
            data-testid="master-next"
            aria-label="Next"
          >
            <SkipForward className="w-5 h-5 text-white" />
          </button>
        </div>
      </div>
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Compact mobile player — thin horizontal card
// ─────────────────────────────────────────────────────────────────────────
const MobilePlayerCompact = () => {
  const { currentTrack, isPlaying, progressMs, durationMs, play, pause, nextTrack, previousTrack } = useMusic();
  const art = currentTrack?.album_image || currentTrack?.artwork_url || '';
  const name = currentTrack?.name || 'Nothing playing';
  const artist = Array.isArray(currentTrack?.artists) ? currentTrack.artists.join(', ') : (currentTrack?.artist_name || '');
  const pct = durationMs > 0 ? Math.min(100, (progressMs / durationMs) * 100) : 0;

  return (
    <div
      className="flex items-center gap-3 p-3 rounded-2xl bg-zinc-900 border border-zinc-800"
      data-testid="mobile-player-card"
    >
      <div className="w-14 h-14 rounded-lg overflow-hidden flex-shrink-0 bg-zinc-800">
        {art ? (
          <img src={art} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Music2 className="w-5 h-5 text-zinc-600" />
          </div>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-white text-sm font-bold truncate" style={{ fontFamily: 'Outfit' }}>{name}</p>
        <div className="flex items-center gap-2">
          <p className="text-zinc-400 text-xs truncate flex-1 min-w-0">{artist || '—'}</p>
          <DeviceBadge compact />
        </div>
        <div className="mt-1 h-0.5 bg-zinc-800 rounded-full overflow-hidden">
          <div className="h-full bg-fuchsia-500" style={{ width: `${pct}%` }} />
        </div>
      </div>
      <div className="flex items-center gap-1 flex-shrink-0">
        <button
          onClick={() => previousTrack?.()}
          className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Previous"
        >
          <SkipBack className="w-3.5 h-3.5 text-white" />
        </button>
        <button
          onClick={() => (isPlaying ? pause?.() : play?.())}
          className="w-11 h-11 rounded-full bg-fuchsia-500 flex items-center justify-center shadow-[0_0_16px_rgba(217,70,239,0.5)] active:scale-95 transition-transform"
          aria-label={isPlaying ? 'Pause' : 'Play'}
        >
          {isPlaying ? <Pause className="w-4 h-4 fill-current text-black" /> : <Play className="w-4 h-4 fill-current text-black ml-0.5" />}
        </button>
        <button
          onClick={() => nextTrack?.()}
          className="w-9 h-9 rounded-full bg-zinc-800 flex items-center justify-center active:scale-95 transition-transform"
          aria-label="Next"
        >
          <SkipForward className="w-3.5 h-3.5 text-white" />
        </button>
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Search bar — debounced Apple search + one-tap queue
// ─────────────────────────────────────────────────────────────────────────
const INCLUDE_VIDEOS_LS_KEY = 'mb.booth.searchIncludeVideos';

const BoothSearchBar = ({ compact = false }) => {
  const { injectNext } = useMusic();
  const [q, setQ] = useState('');
  const [results, setResults] = useState([]);
  const [busy, setBusy] = useState(false);
  const [addingId, setAddingId] = useState(null);
  const [includeVideos, setIncludeVideos] = useState(() => {
    try { return window.localStorage.getItem(INCLUDE_VIDEOS_LS_KEY) === '1'; }
    catch { return false; }
  });
  const debounceRef = useRef(null);

  useEffect(() => {
    try { window.localStorage.setItem(INCLUDE_VIDEOS_LS_KEY, includeVideos ? '1' : '0'); }
    catch { /* private-mode / storage-blocked — non-fatal */ }
  }, [includeVideos]);

  useEffect(() => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    if (!q.trim()) { setResults([]); return; }
    debounceRef.current = setTimeout(async () => {
      setBusy(true);
      try {
        const params = { q: q.trim(), limit: 8 };
        if (includeVideos) params.include_videos = true;
        const r = await axios.get(`${API}/music/search`, { params });
        setResults(r.data?.tracks || []);
      } catch { setResults([]); }
      finally { setBusy(false); }
    }, 300);
    return () => clearTimeout(debounceRef.current);
  }, [q, includeVideos]);

  const addToQueue = async (t) => {
    setAddingId(t.id);
    try {
      await injectNext(t);
      toast.success(`Queued: ${t.name}`);
      setQ(''); setResults([]);
    } catch { toast.error('Could not queue that track'); }
    finally { setAddingId(null); }
  };

  const inputH = compact ? 'h-11' : 'h-14';
  const inputPad = compact ? 'pl-11 text-sm' : 'pl-14 text-base';
  const iconPos = compact ? 'left-4 w-4 h-4' : 'left-5 w-5 h-5';

  return (
    <div className="relative flex-1" data-testid="control-booth-search">
      <Search className={`absolute top-1/2 -translate-y-1/2 text-zinc-500 pointer-events-none ${iconPos}`} />
      <input
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder={compact ? 'Add a song to the queue…' : 'Search or paste a link to queue a track…'}
        className={`w-full ${inputH} ${inputPad} pr-24 bg-zinc-900 border border-zinc-800 rounded-full text-white outline-none focus:border-fuchsia-500 focus:ring-1 focus:ring-fuchsia-500/40 placeholder:text-zinc-600`}
        style={{ fontFamily: 'Outfit' }}
        data-testid="control-booth-search-input"
      />
      <button
        type="button"
        onClick={() => setIncludeVideos((v) => !v)}
        className={`absolute right-3 top-1/2 -translate-y-1/2 inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider transition-colors border ${
          includeVideos
            ? 'bg-fuchsia-500/20 text-fuchsia-300 border-fuchsia-500/50'
            : 'bg-zinc-800/80 text-zinc-500 border-zinc-700 hover:text-zinc-300'
        }`}
        data-testid="booth-search-include-videos-toggle"
        title={includeVideos
          ? 'Videos on — DJ remixes, covers, lyric videos will surface. Click to switch back to clean ATV only.'
          : 'Include videos — surface DJ remixes, covers, lyric videos on top of Topic-channel songs.'}
      >
        <VideoIcon className="w-3 h-3" />
        {includeVideos ? 'Videos' : 'Songs only'}
      </button>
      {busy && (
        <Loader2 className="absolute right-24 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500 animate-spin" />
      )}
      <AnimatePresence>
        {results.length > 0 && (
          <motion.div
            initial={{ opacity: 0, y: -6 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -6 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-full mt-2 rounded-2xl bg-zinc-900 border border-zinc-800 overflow-hidden z-30 max-h-[60vh] overflow-y-auto shadow-2xl"
            data-testid="control-booth-search-results"
          >
            {results.map((t) => (
              <button
                key={t.id}
                onClick={() => addToQueue(t)}
                disabled={!!addingId}
                className="w-full flex items-center gap-3 px-4 py-3 hover:bg-zinc-800 transition-colors text-left disabled:opacity-50"
                data-testid={`search-add-${t.id}`}
              >
                {t.album_image ? (
                  <img src={t.album_image} alt="" className="w-10 h-10 rounded object-cover flex-shrink-0" />
                ) : (
                  <div className="w-10 h-10 rounded bg-zinc-800 flex items-center justify-center flex-shrink-0">
                    <Music2 className="w-4 h-4 text-zinc-600" />
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="text-white text-sm font-medium truncate">{t.name}</p>
                  <p className="text-zinc-500 text-xs truncate">
                    {(Array.isArray(t.artists) ? t.artists.join(', ') : t.artist_name) || ''}
                  </p>
                </div>
                {addingId === t.id ? (
                  <Loader2 className="w-4 h-4 text-fuchsia-400 animate-spin" />
                ) : (
                  <span className="text-[10px] uppercase tracking-widest font-mono text-fuchsia-400">Queue</span>
                )}
              </button>
            ))}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Playlist state — shared across Picker + SongList
// ─────────────────────────────────────────────────────────────────────────
const usePlaylistState = () => {
  const { playTracks, injectNext, queuePlaylistId } = useMusic();
  const [schedule, setSchedule] = useState({});
  const [currentSlotId, setCurrentSlotId] = useState(null);
  const [selectedId, setSelectedId] = useState(null);
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [tracksLoading, setTracksLoading] = useState(false);
  const [autoPlayOnLoad, setAutoPlayOnLoad] = useState(false);

  const todayPlaylists = useMemo(() => {
    const day = todayKey();
    const slots = schedule?.[day] || {};
    const out = [];
    const seen = new Set();
    Object.entries(slots).forEach(([slot, pl]) => {
      if (pl && pl.id && !seen.has(pl.id)) {
        seen.add(pl.id);
        out.push({ ...pl, _slot: slot });
      }
    });
    return out;
  }, [schedule]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/playlists/my-schedule`);
        if (!mounted) return;
        setSchedule(r.data?.schedule || {});
        setCurrentSlotId(r.data?.current?.playlist?.id || null);
      } catch { /* handled in render */ }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, []);

  // The tile the picker highlights must name the playlist the venue is
  // actually hearing, because the song list under it is clickable and a
  // click plays from that list. Follow the live queue's playlist first,
  // then the slot for right now (the backend computes it and we used to
  // throw it away), then the first tile of the day.
  useEffect(() => {
    if (!todayPlaylists.length) return;
    const scheduled = (id) => !!id && todayPlaylists.some((p) => p.id === id);
    // ...but never while an operator pick is still loading, or this
    // would drag them back to the playlist they just chose to leave.
    if (scheduled(queuePlaylistId) && !autoPlayOnLoad) {
      if (queuePlaylistId !== selectedId) setSelectedId(queuePlaylistId);
      return;
    }
    if (!selectedId) {
      setSelectedId(scheduled(currentSlotId) ? currentSlotId : todayPlaylists[0].id);
    }
  }, [todayPlaylists, selectedId, queuePlaylistId, currentSlotId, autoPlayOnLoad]);

  useEffect(() => {
    if (!selectedId) { setTracks([]); return; }
    let mounted = true;
    (async () => {
      setTracksLoading(true);
      try {
        const r = await axios.get(`${API}/playlists/curated/${selectedId}`);
        if (!mounted) return;
        const list = r.data?.tracks || [];
        setTracks(list);
        if (autoPlayOnLoad && list.length > 0) {
          setAutoPlayOnLoad(false);
          try { await playTracks(list, 0, { playlist_id: selectedId }); }
          catch { /* toast in playTracks */ }
        }
      } catch { if (mounted) setTracks([]); }
      // Always disarm autoplay. A failed fetch or an empty playlist used to
      // leave the flag set, so the next selection change started playback
      // the operator never asked for.
      finally { if (mounted) { setTracksLoading(false); setAutoPlayOnLoad(false); } }
    })();
    return () => { mounted = false; };
  }, [selectedId, autoPlayOnLoad, playTracks]);

  const pickPlaylist = useCallback((plId) => {
    if (plId === selectedId && tracks.length > 0) {
      playTracks(tracks, 0, { playlist_id: plId }).catch(() => {});
    } else {
      setAutoPlayOnLoad(true);
      setSelectedId(plId);
    }
  }, [selectedId, tracks, playTracks]);

  const playFromIndex = useCallback(async (idx) => {
    if (!tracks.length) return;
    try { await playTracks(tracks, idx, { playlist_id: selectedId }); }
    catch (e) { toast.error('Could not start playback'); console.warn('[Booth] playTracks failed:', e?.message); }
  }, [tracks, playTracks, selectedId]);

  const queueTrack = useCallback(async (t) => {
    try { await injectNext(t); toast.success(`Queued: ${t.name}`); }
    catch { toast.error('Could not queue that track'); }
  }, [injectNext]);

  const selected = todayPlaylists.find((p) => p.id === selectedId);

  return {
    todayPlaylists, selectedId, tracks, selected,
    loading, tracksLoading,
    pickPlaylist, playFromIndex, queueTrack,
  };
};

// ─────────────────────────────────────────────────────────────────────────
// Song List — the tracks inside the currently selected playlist
// ─────────────────────────────────────────────────────────────────────────
const SongList = ({
  selected, tracks, loading, tracksLoading,
  todayPlaylistsCount, onPlayIndex, onQueue,
  currentTrackId, fill = true, collapsible = false, defaultOpen = true,
}) => {
  const [open, setOpen] = useState(defaultOpen);
  const bodyOpen = !collapsible || open;

  const HeaderInner = (
    <div className="min-w-0 flex-1 text-left">
      <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Now Spinning</span>
      <h3 className="text-lg font-bold truncate" style={{ fontFamily: 'Outfit' }}>
        {selected ? selected.name : 'Playlist tracks'}
        {selected && (
          <span className="text-zinc-500 font-mono text-sm" data-testid="playlist-track-count"> · {tracks.length} tracks</span>
        )}
      </h3>
    </div>
  );

  return (
    <section
      className={`bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col ${fill ? 'h-full min-h-0' : ''}`}
      data-testid="control-booth-playlist-tracks"
    >
      {collapsible ? (
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0 w-full hover:bg-zinc-800/40 transition-colors"
          data-testid="booth-queue-toggle"
          aria-expanded={open}
        >
          {HeaderInner}
          <ChevronDown className={`w-4 h-4 text-zinc-400 flex-shrink-0 transition-transform ${open ? 'rotate-180' : ''}`} />
        </button>
      ) : (
        <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
          {HeaderInner}
          <ListMusic className="w-4 h-4 text-zinc-500 flex-shrink-0" />
        </div>
      )}
      {bodyOpen && (
        <div className={`overflow-y-auto min-h-0 ${fill ? 'flex-1' : (collapsible ? 'max-h-[55vh]' : '')}`}>
          {loading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>
          ) : todayPlaylistsCount === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">
              No playlists scheduled for today. Set them up in{' '}
              <span className="text-fuchsia-400">Playlists → Schedule</span>.
            </div>
          ) : tracksLoading ? (
            <div className="flex items-center justify-center py-10"><Loader2 className="w-5 h-5 text-zinc-500 animate-spin" /></div>
          ) : tracks.length === 0 ? (
            <div className="p-6 text-center text-zinc-500 text-sm">This playlist is empty.</div>
          ) : (
            <ul>
              {tracks.map((t, idx) => {
                const active = currentTrackId && t.id === currentTrackId;
                return (
                  <li
                    key={t.id || idx}
                    onClick={() => onPlayIndex(idx)}
                    className={`group flex items-center gap-4 px-5 py-3 border-b border-zinc-800/50 hover:bg-zinc-800/50 transition-colors cursor-pointer ${
                      active ? 'bg-fuchsia-500/5 border-l-4 border-l-fuchsia-500' : ''
                    }`}
                    data-testid={`playlist-row-${idx}`}
                  >
                    <span className="text-xs font-mono text-zinc-500 w-6 text-right">{String(idx + 1).padStart(2, '0')}</span>
                    <div className="flex-1 min-w-0">
                      <p className={`text-sm font-semibold truncate ${active ? 'text-fuchsia-400' : 'text-white'}`} style={{ fontFamily: 'Outfit' }}>
                        {t.name}
                      </p>
                      <p className="text-xs text-zinc-500 truncate">
                        {(Array.isArray(t.artists) ? t.artists.join(', ') : t.artist_name) || ''}
                      </p>
                    </div>
                    {active && <AudioLines className="w-4 h-4 text-fuchsia-400 animate-pulse flex-shrink-0" />}
                    <button
                      onClick={(e) => { e.stopPropagation(); onQueue(t); }}
                      className="opacity-0 group-hover:opacity-100 text-[10px] uppercase tracking-widest font-mono px-2 py-1 rounded text-fuchsia-400 hover:bg-fuchsia-500/10 transition-all flex-shrink-0"
                      data-testid={`playlist-queue-${idx}`}
                    >
                      Queue
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      )}
    </section>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Vibe Picker — horizontal image tiles
// ─────────────────────────────────────────────────────────────────────────
const VibePicker = ({ todayPlaylists, selectedId, onPick, size = 'lg' }) => {
  if (todayPlaylists.length === 0) return null;
  const w = size === 'lg' ? 'w-48 h-24' : 'w-40 h-20';
  return (
    <div className="flex flex-col" data-testid="control-booth-playlist-picker">
      <div className="flex items-center justify-between mb-2">
        <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">
          Today's Vibes · {new Date().toLocaleDateString(undefined, { weekday: 'long' })}
        </span>
        <span className="text-[10px] text-zinc-600 font-mono hidden md:inline">← swipe →</span>
      </div>
      <div className="flex gap-3 overflow-x-auto pb-1 -mx-1 px-1 snap-x scrollbar-thin">
        {todayPlaylists.map((pl) => {
          const active = pl.id === selectedId;
          return (
            <button
              key={pl.id}
              onClick={() => onPick(pl.id)}
              className={`relative flex-shrink-0 ${w} rounded-2xl overflow-hidden border transition-all hover:scale-[1.02] active:scale-95 snap-start ${
                active ? 'border-fuchsia-500 shadow-[0_0_20px_rgba(217,70,239,0.4)]' : 'border-zinc-800 hover:border-zinc-600'
              }`}
              data-testid={`picker-${pl.id}`}
            >
              {pl.image_url ? (
                <img src={pl.image_url} alt="" className="absolute inset-0 w-full h-full object-cover" />
              ) : (
                <div className="absolute inset-0 bg-gradient-to-br from-fuchsia-900/40 to-black" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-black/30" />
              <div className="absolute inset-0 p-3 flex flex-col justify-end text-left">
                <p className="text-white font-bold text-sm leading-tight line-clamp-1" style={{ fontFamily: 'Outfit' }}>{pl.name}</p>
                <div className="flex items-center gap-2">
                  <p className="text-zinc-400 text-[10px] font-mono uppercase">{pl._slot}</p>
                  {typeof pl.track_count === 'number' && (
                    <p className="text-zinc-500 text-[10px] font-mono">· {pl.track_count} tracks</p>
                  )}
                </div>
              </div>
              {active && (
                <div className="absolute top-2 right-2 px-2 py-0.5 rounded-full bg-fuchsia-500 text-black text-[9px] font-bold tracking-wider">LIVE</div>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Celebration Modal
// ─────────────────────────────────────────────────────────────────────────
const CelebrationModal = ({ open, onClose }) => {
  const { injectNext } = useMusic();
  const [tracks, setTracks] = useState([]);
  const [loading, setLoading] = useState(false);
  const [firingId, setFiringId] = useState(null);

  useEffect(() => {
    if (!open) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      try {
        const r = await axios.get(`${API}/venue/celebrations`);
        if (mounted) setTracks(r.data?.tracks || []);
      } catch { if (mounted) setTracks([]); }
      finally { if (mounted) setLoading(false); }
    })();
    return () => { mounted = false; };
  }, [open]);

  const fire = async (t) => {
    setFiringId(t.id);
    try {
      await injectNext(t);
      toast.success(`🎉 ${t.name} — plays next!`);
      onClose();
    } catch { toast.error('Could not queue the celebration'); }
    finally { setFiringId(null); }
  };

  const iconFor = (t) => {
    const n = (t?.name || '').toLowerCase();
    if (n.includes('birthday')) return Cake;
    return PartyPopper;
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm"
          data-testid="celebration-modal-backdrop"
        >
          <motion.div
            initial={{ scale: 0.9, y: 20 }}
            animate={{ scale: 1, y: 0 }}
            exit={{ scale: 0.9, y: 20 }}
            transition={{ type: 'spring', stiffness: 220, damping: 22 }}
            onClick={(e) => e.stopPropagation()}
            className="w-full max-w-md rounded-3xl bg-zinc-900 border border-zinc-800 p-6 shadow-[0_0_40px_rgba(236,72,153,0.25)]"
            data-testid="celebration-modal"
          >
            <div className="flex items-center justify-between mb-5">
              <div className="flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-pink-400" />
                <h2 className="text-white font-black text-xl" style={{ fontFamily: 'Outfit' }}>Celebration Hub</h2>
              </div>
              <button
                onClick={onClose}
                className="w-8 h-8 rounded-full bg-zinc-800 hover:bg-zinc-700 text-zinc-400 flex items-center justify-center transition-colors"
                data-testid="celebration-modal-close"
                aria-label="Close"
              >
                <X className="w-4 h-4" />
              </button>
            </div>
            <p className="text-zinc-400 text-sm mb-5">
              Plays right after the current track — priority-1 injection, no queue rearranging.
            </p>

            {loading ? (
              <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 text-pink-400 animate-spin" /></div>
            ) : tracks.length === 0 ? (
              <p className="text-center text-zinc-500 text-sm py-8">Couldn't load celebrations. Try again in a moment.</p>
            ) : (
              <div className="space-y-3">
                {tracks.map((t) => {
                  const Icon = iconFor(t);
                  const busy = firingId === t.id;
                  const kind = (t?.name || '').toLowerCase().includes('birthday') ? 'birthday' : 'congrats';
                  return (
                    <button
                      key={t.id}
                      onClick={() => fire(t)}
                      disabled={!!firingId}
                      className="w-full flex items-center gap-4 p-4 rounded-2xl bg-zinc-800/60 border border-zinc-800 text-left transition-colors disabled:opacity-50 hover:bg-zinc-800"
                      data-testid={`celebration-${kind}`}
                    >
                      <div className="w-12 h-12 rounded-full flex items-center justify-center flex-shrink-0 bg-pink-500 shadow-[0_0_16px_rgba(236,72,153,0.5)]">
                        <Icon className="w-5 h-5 text-white" strokeWidth={2.5} />
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-white font-semibold text-base truncate" style={{ fontFamily: 'Outfit' }}>{t.name}</p>
                        <p className="text-zinc-400 text-xs truncate">
                          {(Array.isArray(t.artists) ? t.artists.join(', ') : t.artist_name) || ''}
                        </p>
                      </div>
                      {busy ? (
                        <Loader2 className="w-4 h-4 text-pink-400 animate-spin" />
                      ) : (
                        <span className="text-[10px] uppercase tracking-widest font-mono text-pink-400">Play next</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

// ─────────────────────────────────────────────────────────────────────────
// Root layout
// ─────────────────────────────────────────────────────────────────────────
const ControlBoothPage = () => {
  const { user } = useAuth();
  const { currentTrack, queue: liveQueue, playTracks, queuePlaylistId } = useMusic();
  const [celebrationOpen, setCelebrationOpen] = useState(false);
  const playlist = usePlaylistState();
  // Defense-in-depth: AI-typed venues use the in-house AI booth, never the
  // commercial YouTube-driven booth. If they somehow land here (bookmark,
  // stale bundle, race on /auth/me), bounce them to the correct home.
  if (user?.client_type === 'ai') {
    return <Navigate to="/" replace />;
  }
  const currentTrackId = currentTrack?.id;
  // Show the CURRENTLY PLAYING queue in the center panel whenever something
  // is actually loaded/spinning — regardless of whether it came from a
  // scheduled playlist, a remote take-over, or guest requests. Previously the
  // panel only showed a live queue when it matched the highlighted scheduled
  // playlist, so remote/guest/ad-hoc playback left it saying "No playlists
  // scheduled" even while music was playing (the reported issue).
  const hasLive = Array.isArray(liveQueue) && liveQueue.length > 0;
  // If the live queue is attributed to one of today's playlists, use that for
  // the header; otherwise label it "Live Queue".
  const liveQueuePlaylist = hasLive && queuePlaylistId
    ? playlist.todayPlaylists.find((p) => p.id === queuePlaylistId)
    : undefined;
  const showLive = hasLive;
  const displayTracks = showLive ? liveQueue : playlist.tracks;
  const displaySelected = showLive
    ? (liveQueuePlaylist || { name: 'Live Queue' })
    : playlist.selected;
  // Click-to-play MUST stay attributed to the list actually being shown. When
  // showing the live queue we stamp the queue's OWN playlist id (not the
  // highlighted tile) so clicking a row never hijacks the venue onto a
  // different playlist — that was an earlier bug.
  const displayPlaylistId = showLive ? queuePlaylistId : playlist.selectedId;

  // Play the track the operator actually clicked. The click index maps back
  // into the SAME array the SongList rendered. (Not a hook — kept as a plain
  // fn because it sits after the conditional early-return above.)
  const handlePlayIndex = (idx) => {
    const list = displayTracks;
    if (!Array.isArray(list) || !list.length) return;
    playTracks(list, idx, { playlist_id: displayPlaylistId }).catch((e) => {
      toast.error('Could not start playback');
      console.warn('[Booth] playTracks failed:', e?.message);
    });
  };

  return (
    <Layout hidePlayerBarBelowXl>
      <div
        className="min-h-screen bg-[#09090B] text-white content-scroll"
        data-testid="control-booth-page"
      >
        {/* ==============================================================
            MOBILE / TABLET LAYOUT (< xl) — Jun 2026 redesign
            Natural vertical scroll:
              · Q1   search + celebrate (sticky)
              · big album-art Master Player
              · Guest requests (shrinks to content)
              · Live Queue (collapsible, collapsed by default)
              · Vibe picker (inline)
            The global bottom PlayerBar is hidden here (see <Layout
            hidePlayerBarBelowXl/>) because this page now shows the full
            album-art player inline.
            ============================================================== */}
        <div className="xl:hidden flex flex-col gap-3 p-3" data-testid="mobile-booth-v2">
          {/* Q1 — Search + Celebrate (sticky) */}
          <div
            className="flex items-center gap-2 sticky top-0 z-20 -mx-3 px-3 py-2 bg-[#09090B]/95 backdrop-blur"
            data-testid="mobile-q1"
          >
            <BoothSearchBar compact />
            <button
              onClick={() => setCelebrationOpen(true)}
              className="w-11 h-11 flex-shrink-0 rounded-full bg-pink-500 hover:bg-pink-400 flex items-center justify-center shadow-[0_0_20px_rgba(236,72,153,0.45)] active:scale-95 transition-transform"
              data-testid="celebration-open-fab"
              aria-label="Celebrate"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
            </button>
            <AlwaysOnMode compact />
          </div>

          {/* Big album-art Master Player */}
          <MasterPlayer fill={false} />

          {/* Guest requests — shrinks to content, scrolls only when long */}
          <section
            className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col"
            data-testid="mobile-q2-requests"
          >
            <div className="px-4 py-2 flex items-center gap-2 border-b border-zinc-800/70 flex-shrink-0">
              <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse" />
              <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Live Requests</span>
            </div>
            <div className="max-h-[40vh] overflow-y-auto px-3 py-2">
              <RequestsInbox />
            </div>
          </section>

          {/* Guest shoutouts — approval flow (restored after DJ Console retirement) */}
          <ShoutoutsInbox />

          {/* Live Queue — collapsible, collapsed by default */}
          <SongList
            selected={displaySelected}
            tracks={displayTracks}
            loading={playlist.loading && !showLive}
            tracksLoading={playlist.tracksLoading && !showLive}
            todayPlaylistsCount={showLive ? 1 : playlist.todayPlaylists.length}
            onPlayIndex={handlePlayIndex}
            onQueue={playlist.queueTrack}
            currentTrackId={currentTrackId}
            fill={false}
            collapsible
            defaultOpen={false}
          />

          {/* Vibe picker — inline */}
          {playlist.todayPlaylists.length > 0 && (
            <div className="pb-2" data-testid="mobile-picker">
              <VibePicker
                todayPlaylists={playlist.todayPlaylists}
                selectedId={playlist.selectedId}
                onPick={playlist.pickPlaylist}
                size="sm"
              />
            </div>
          )}
        </div>

        {/* ==============================================================
            DESKTOP / TABLET LAYOUT (xl+)
            ============================================================== */}
        <div className="hidden xl:grid xl:grid-cols-12 gap-4 p-4 min-h-screen">
          {/* Header — welcome + search + celebrate */}
          <div className="xl:col-span-12 flex items-center gap-3">
            <div className="min-w-0 flex-shrink-0 pr-2">
              <p className="text-[10px] uppercase tracking-[0.35em] text-zinc-500 font-mono">Control Booth</p>
              <h1 className="text-2xl font-black tracking-tight truncate" style={{ fontFamily: 'Outfit' }}>
                {user?.name || 'Live'}
              </h1>
            </div>
            <BoothSearchBar />
            <button
              onClick={() => setCelebrationOpen(true)}
              className="h-14 px-6 rounded-full bg-pink-500 hover:bg-pink-400 text-white font-bold flex items-center gap-2 shadow-[0_0_30px_rgba(236,72,153,0.5)] hover:shadow-[0_0_40px_rgba(236,72,153,0.8)] hover:scale-[1.02] active:scale-95 transition-[transform,box-shadow] flex-shrink-0"
              data-testid="celebration-open-header"
            >
              <Sparkles className="w-5 h-5 animate-pulse" />
              <span className="tracking-wider" style={{ fontFamily: 'Outfit' }}>CELEBRATE</span>
            </button>
            <AlwaysOnMode />
          </div>

          {/* Middle row — Player | Song List | Inbox */}
          <div className="xl:col-span-3 min-h-[560px]">
            <MasterPlayer />
          </div>
          <div className="xl:col-span-5 min-h-[560px]">
            <SongList
              selected={displaySelected}
              tracks={displayTracks}
              loading={playlist.loading && !showLive}
              tracksLoading={playlist.tracksLoading && !showLive}
              todayPlaylistsCount={showLive ? 1 : playlist.todayPlaylists.length}
              onPlayIndex={handlePlayIndex}
              onQueue={playlist.queueTrack}
              currentTrackId={currentTrackId}
            />
          </div>
          <div className="xl:col-span-4 flex flex-col gap-3">
            <section
              className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden flex flex-col max-h-[52vh]"
              data-testid="control-booth-inbox"
            >
              <div className="px-5 py-3 border-b border-zinc-800 flex items-center justify-between flex-shrink-0">
                <div className="flex items-center gap-2">
                  <span className="w-2 h-2 bg-cyan-400 rounded-full animate-pulse shadow-[0_0_10px_rgba(6,182,212,0.8)]" />
                  <span className="text-[10px] tracking-[0.3em] uppercase text-zinc-500 font-mono">Live</span>
                </div>
                <h3 className="text-lg font-bold" style={{ fontFamily: 'Outfit' }}>Requests</h3>
              </div>
              <div className="flex-1 min-h-0 overflow-y-auto p-3">
                <RequestsInbox />
              </div>
            </section>
            {/* Guest shoutouts — approval flow (restored after DJ Console retirement) */}
            <ShoutoutsInbox />
          </div>

          {/* Bottom — Vibe picker strip */}
          <div className="xl:col-span-12">
            <VibePicker
              todayPlaylists={playlist.todayPlaylists}
              selectedId={playlist.selectedId}
              onPick={playlist.pickPlaylist}
            />
          </div>
        </div>

        <CelebrationModal open={celebrationOpen} onClose={() => setCelebrationOpen(false)} />
      </div>
    </Layout>
  );
};

export default ControlBoothPage;
